import type { DateValidationError } from "@mui/x-date-pickers-pro";
import type { Locale } from "date-fns";
import { format, getYear, isAfter, isBefore, isMatch, isValid, parse, startOfDay } from "date-fns";
import { getUserLocaleInfo } from "../../../utilities/userLocaleConfig";

type LocaleFormatConfig = {
  parseFormat: string;
  displayFormat: string;
  separator: string;
  placeholder: string;
};

export const DATE_ISO_FORMAT = "yyyy-MM-dd";

const isShortYear = (year: string) => year.length < 4;

const formatToFullYear = (year: string): number => {
  // Pad with zeros to 4 digits, then set first digit to "2": 1→2001, 10→2010, 123→2123
  const padded = year.padStart(4, "0");
  return parseInt("2" + padded.slice(1));
};

const isYearValid = (date: Date) => {
  const year = getYear(date);
  return year >= 1900 && year < 2100;
};

const isDateLengthValid = (dateStr: string) => {
  return dateStr.length === 10;
};

const handleIsoFormat = (dateStr: string) => {
  const date = parse(dateStr, DATE_ISO_FORMAT, new Date());
  const valid = isValid(date) && isDateLengthValid(dateStr) && isYearValid(date);
  if (valid) {
    return date;
  }
  return undefined;
};

/**
 * Normalizes a date input string according to the specified locale format.
 *
 * - Accepts any separator (/, ., -)
 * - Expands short years to full 4-digit years (1→2001, 20→2020, 99→2099)
 * - Pads day and month to 2 digits (1→01, 9→09)
 * - Converts separator to the locale's native separator
 * - Validates month (1-12), day (1-31), and year (1900-2099)
 *
 * @example
 * // en-US (MM/DD/YYYY)
 * normalizeDateInput("1/31/20", enUS) // "01/31/2020"
 * normalizeDateInput("1.5.20", enUS)  // "01/05/2020"
 *
 * // sv (YYYY-MM-DD)
 * normalizeDateInput("20-1-31", sv)   // "2020-01-31"
 * normalizeDateInput("20/1/5", sv)    // "2020-01-05"
 *
 * // de (DD.MM.YYYY)
 * normalizeDateInput("31/1/20", de)   // "31.01.2020"
 *
 * @returns Normalized date string in locale format, or undefined if invalid
 */
export const normalizeDateInput = (dateStr: string, locale: Locale): string | undefined => {
  const separators = ["/", ".", "-"];
  let parts: string[] = [];

  for (const sep of separators) {
    if (dateStr.includes(sep)) {
      parts = dateStr.split(sep);
      break;
    }
  }

  if (parts.length !== 3) {
    return undefined;
  }

  const order = getDatePartOrder(locale);
  const yearIndex = order.indexOf("year");
  const monthIndex = order.indexOf("month");
  const dayIndex = order.indexOf("day");

  if (yearIndex === -1 || monthIndex === -1 || dayIndex === -1) {
    return undefined;
  }

  const yearPart = parts[yearIndex];
  const monthPart = parts[monthIndex];
  const dayPart = parts[dayIndex];

  if (!yearPart || !monthPart || !dayPart) {
    return undefined;
  }

  // Validate month and day are valid numbers
  const monthNum = parseInt(monthPart, 10);
  const dayNum = parseInt(dayPart, 10);

  if (isNaN(monthNum) || isNaN(dayNum) || monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
    return undefined;
  }

  // Expand short year
  const fullYear = isShortYear(yearPart) ? formatToFullYear(yearPart).toString() : yearPart;

  // Validate year
  const yearNum = parseInt(fullYear, 10);
  if (isNaN(yearNum) || yearNum < 1900 || yearNum >= 2100) {
    return undefined;
  }

  // Build normalized parts array with expanded year and padded day/month
  const normalizedParts = [...parts];
  normalizedParts[yearIndex] = fullYear;
  normalizedParts[monthIndex] = monthNum.toString().padStart(2, "0");
  normalizedParts[dayIndex] = dayNum.toString().padStart(2, "0");

  const formatConfig = getFormatConfigForLocale(locale);
  const normalizedInput = normalizedParts.join(formatConfig.separator);
  return normalizedInput;
};

export const validateDateRange = (value: Date, minDate?: Date, maxDate?: Date): DateValidationError | null => {
  if (minDate && isValid(minDate) && isBefore(value, minDate)) {
    return "minDate";
  }
  if (maxDate && isValid(maxDate) && isAfter(value, maxDate)) {
    return "maxDate";
  }
  return null;
};

export const getDisplayFormatForLocale = (locale: Locale): string => {
  const formatConfig = getFormatConfigForLocale(locale);
  return formatConfig.displayFormat;
};
const formatConfigCache = new Map<Locale, LocaleFormatConfig>();

const getDatePartOrder = (locale: Locale): Array<"day" | "month" | "year"> => {
  // Use a test date where all parts are unique and identifiable
  // Day: 15, Month: 03, Year: 2021 (or 21 for 2-digit year formats)
  // These values don't overlap: 15 (day), 3 (month), 2021/21 (year)
  const testDate = new Date(2021, 2, 15);
  const formatted = format(testDate, "P", { locale });

  // Split by non-digit characters
  const parts = formatted.split(/\D+/).filter(Boolean);

  const order: Array<"day" | "month" | "year"> = [];

  for (const part of parts) {
    const num = parseInt(part, 10);
    if (num === 2021 || num === 21) {
      order.push("year");
    } else if (num === 3) {
      order.push("month");
    } else if (num === 15) {
      order.push("day");
    }
  }

  return order;
};

const getDateSeparator = (locale: Locale): string => {
  const testDate = new Date(2025, 0, 15);
  const formatted = format(testDate, "P", { locale });
  const separatorMatch = formatted.match(/[^\d]/);
  return separatorMatch ? separatorMatch[0] : "/";
};

const buildDateFormat = (
  order: Array<"day" | "month" | "year">,
  separator: string,
  isDisplayFormat: boolean
): string => {
  const formatMap: Record<"day" | "month" | "year", string> = {
    day: isDisplayFormat ? "dd" : "d",
    month: isDisplayFormat ? "MM" : "M",
    year: "yyyy",
  };

  return order.map((part) => formatMap[part]).join(separator);
};

const buildPlaceholder = (order: Array<"day" | "month" | "year">, separator: string): string => {
  const placeholderMap: Record<"day" | "month" | "year", string> = {
    day: "DD",
    month: "MM",
    year: "YYYY",
  };

  return order.map((part) => placeholderMap[part]).join(separator);
};

const getFormatConfigForLocale = (locale: Locale): LocaleFormatConfig => {
  if (formatConfigCache.has(locale)) {
    return formatConfigCache.get(locale)!;
  }

  const order = getDatePartOrder(locale);
  const separator = getDateSeparator(locale);

  const parseFormat = buildDateFormat(order, separator, false);
  const displayFormat = buildDateFormat(order, separator, true);
  const placeholder = buildPlaceholder(order, separator);

  const config: LocaleFormatConfig = {
    parseFormat,
    displayFormat,
    separator,
    placeholder,
  };

  formatConfigCache.set(locale, config);

  return config;
};

export const getPlaceholderForLocale = (locale: Locale): string => {
  const formatConfig = getFormatConfigForLocale(locale);
  return formatConfig.placeholder;
};

export const parseIsoOrLocalFormat = (value: string) => {
  if (isMatch(value, DATE_ISO_FORMAT)) {
    return handleIsoFormat(value);
  }

  return handleLocaleFormat(value, getUserLocaleInfo().locale);
};

export const handleLocaleFormat = (dateStr: string, locale: Locale) => {
  const formatConfig = getFormatConfigForLocale(locale);
  const normalizedInput = normalizeDateInput(dateStr, locale);
  if (!normalizedInput) {
    return undefined;
  }
  const date = parse(normalizedInput, formatConfig.parseFormat, new Date());
  const valid = isValid(date);

  if (valid) {
    return startOfDay(date);
  }
  return undefined;
};
